import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import QRCode from "qrcode";
import { getBalanceAndPin } from "../../features/balance-and-pin/balanceAndPinSlice";
import { getDasDeviceDetails } from "../../features/das-device-manager/dasDeviceManagerSlice";
import { getWeatherDataAction } from "../../features/weather-widget/weatherWidgetSlice";
import {
  activateWearableTicket,
  getGuestTickets,
} from "../../features/event-tickets/eventTicketsSlice";

import Skiing from "./Skiing";
import Balance from "./Balance";
import YourStay from "./YourStay";
import Memories from "./Memories";
import WeatherWidget from "./WeatherWidget";
import Spinner from "../../components/Spinner";

import ticketLogo from "./imgs/ticket-logo.svg";

import {
  MainContainer,
  NavBar,
  IntroContainer,
  ScheduleContainer,
  DiscoverContainer,
  RacesContainer,
  GetToKnowContainer,
  LanguageGuideContainer,
  FooterContainer,
  Separator,
  ScheduleItem,
  ScheduleInfoContainer,
  DiscoverItem,
  TicketContainer,
} from "./styles";

import logo from "./imgs/nav-logos.svg";
import yourStay from "./imgs/your-stay.webp";
import skiing from "./imgs/skiing.webp";
import balance from "./imgs/balance.webp";
import memories from "./imgs/memories.webp";
import getToKnow from "./imgs/get-to-know.webp";
import localAttractions from "./imgs/local-attractions.webp";
import poweredBy from "./imgs/digiseq-logo.svg";
import copyIconWhite from "./imgs/copy-icon-white.svg";
import paymeswissLogo from "./imgs/paymeswiss_logo.svg";

import packageJson from "../../../package.json";
import { usePromoTracking } from "../../hooks/usePromoTracking";

const localDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// tickets are per day (yyyy-MM-dd in the event time zone); the backend has the final say on activate
const TICKETS_TIME_ZONE = "Europe/Vienna";

const todayInTimeZone = (timeZone) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const pickTodaysTicket = (tickets, today) => {
  const list = Array.isArray(tickets) ? tickets : [];
  const datedToday = list.find((ticket) => ticket?.date === today);
  if (datedToday) return datedToday;
  const undated = list.filter((ticket) => !ticket?.date);
  return (
    undated.find(
      (ticket) => !ticket.active && ticket.remainingRedemptions > 0,
    ) ||
    undated[0] ||
    null
  );
};

const nextTicketDate = (tickets, today) =>
  (Array.isArray(tickets) ? tickets : [])
    .map((ticket) => ticket?.date)
    .filter((date) => date && date > today)
    .sort()[0] || null;

const formatAgendaTime = (startTime) => {
  if (!startTime) return "";
  return String(startTime).slice(0, 5);
};

const RACE_ITINERARY = [
  {
    name: "Super G Streif",
    time: "11:30",
    event: "Friday, 23 January",
  },
  {
    name: "DOWNHILL Streif",
    time: "11:30",
    event: "Saturday, 24 January",
  },
  {
    name: "SLALOM 1st run Ganslern*",
    time: "10:15",
    event: "Sunday, 25 January",
  },
  {
    name: "SLALOM 2nd run Ganslern*",
    time: "13:30",
    event: "Sunday, 25 January",
  },
];

const Kitzbuhel26 = () => {
  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get("serialNumber");
  const eventId = searchParams.get("eventId");

  const [copied, setCopied] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [ticketQR, setTicketQR] = useState(null);

  const { trackButtonClick } = usePromoTracking(eventId, "Kitzbuhel 2026");

  const dispatch = useDispatch();
  const { balanceAndPin, isLoading } = useSelector(
    (state) => state.balanceAndPin,
  );
  const { deviceDetails, gettingDeviceDetails } = useSelector(
    (state) => state.dasDevices,
  );
  const { tickets, activatingTicket, activatingTicketError } = useSelector(
    (state) => state.eventTickets,
  );

  const { weatherData, loadingWeather, errorWeather } = useSelector(
    (state) => state.weatherWidget,
  );

  const attendance = deviceDetails?.attendance;
  const guestId = attendance?.guestId;
  const gateTicketCode = attendance?.customFields?.ticketId?.value;
  const gateTicketZone = attendance?.customFields?.zone?.value;
  const ticketsToday = todayInTimeZone(TICKETS_TIME_ZONE);
  const skiTicket = pickTodaysTicket(tickets, ticketsToday);
  const nextSkiTicketDate = skiTicket
    ? null
    : nextTicketDate(tickets, ticketsToday);

  const getHotelInfo = () => {
    const customFields = deviceDetails?.group?.customFields;
    const hotelName = customFields?.hotelName?.value;

    if (!hotelName) {
      return null;
    }

    return {
      options: [
        {
          id: "hotel",
          item: hotelName,
          additionalData: {
            image: customFields?.hotelImage?.value || "",
            address: customFields?.hotelAddress?.value || "",
          },
        },
      ],
    };
  };

  const getWalletURL = () => {
    return (
      attendance?.customFields?.walletUrl?.value ||
      deviceDetails?.group?.customFields?.walletUrl?.value ||
      null
    );
  };

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    dispatch(getWeatherDataAction({ latitude: 47.4464, longitude: 12.3922 }));
  }, [dispatch]);

  useEffect(() => {
    if (isMounted) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied, isMounted]);

  useEffect(() => {
    if (!balanceAndPin && serialNumber) {
      dispatch(
        getBalanceAndPin({
          serial: serialNumber,
          query: "?provider=pannovate",
        }),
      );
    }
  }, [balanceAndPin, serialNumber, dispatch]);

  useEffect(() => {
    if (serialNumber && eventId) {
      dispatch(
        getDasDeviceDetails({
          serial: serialNumber,
          eventId,
          locale: "en",
        }),
      );
    }
  }, [serialNumber, eventId, dispatch]);

  useEffect(() => {
    if (eventId && guestId) {
      dispatch(getGuestTickets({ eventId, guestId }));
    }
  }, [eventId, guestId, dispatch]);

  useEffect(() => {
    const generateQR = async (text) => {
      try {
        const svgString = await QRCode.toString(text, {
          type: "svg",
          width: 200,
          height: 200,
          margin: 1,
        });
        const svgDataUrl = `data:image/svg+xml;base64,${btoa(svgString)}`;
        setTicketQR(svgDataUrl);
      } catch (err) {
        console.error("Error generating QR code:", err);
      }
    };

    if (gateTicketCode) {
      generateQR(gateTicketCode);
    } else {
      setTicketQR(null);
    }
  }, [gateTicketCode]);

  const [isMemoriesOpen, setIsMemoriesOpen] = useState(false);
  const [isBalanceOpen, setIsBalanceOpen] = useState(false);
  const [isSkiingOpen, setIsSkiingOpen] = useState(false);
  const [isYourStayOpen, setIsYourStayOpen] = useState(false);

  const handleActivateTicket = () => {
    if (!eventId || !serialNumber || !skiTicket?.id) return;

    dispatch(
      activateWearableTicket({
        eventId,
        serialNumber,
        ticketId: skiTicket.id,
      }),
    );
  };

  const handleClose = (closeItem) => {
    const discoverContainer = document.getElementById("discover");
    discoverContainer.scrollIntoView({ behavior: "smooth" });

    switch (closeItem) {
      case "memories":
        setIsMemoriesOpen(false);
        break;
      case "balance":
        setIsBalanceOpen(false);
        break;
      case "skiing":
        setIsSkiingOpen(false);
        break;
      case "yourStay":
        setIsYourStayOpen(false);
        break;
      default:
        break;
    }
  };

  const handleOpen = (openItem) => {
    window.scrollTo(0, 0);
    switch (openItem) {
      case "memories":
        setIsMemoriesOpen(true);
        break;
      case "balance":
        setIsBalanceOpen(true);
        break;
      case "skiing":
        setIsSkiingOpen(true);
        break;
      case "yourStay":
        setIsYourStayOpen(true);
        break;
      default:
        break;
    }
  };

  const renderHighlights = () => {
    const days = deviceDetails?.schedules?.[0]?.days || [];
    const today = localDateKey(new Date());
    const todaysDays = days.filter(
      (day) => String(day?.date || "").slice(0, 10) === today,
    );

    if (todaysDays.length === 0) return null;

    return todaysDays.map((day) => {
      const [year, month, dayOfMonth] = String(day.date).slice(0, 10).split("-");
      const formattedDate = new Date(
        Number(year),
        Number(month) - 1,
        Number(dayOfMonth),
      ).toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });

      return (
        <ScheduleItem key={day.id || day.date}>
          <div className="schedule-list">
            <p>{formattedDate}</p>
            <Separator />
            {day.agendaItems?.map((item, index) => {
              const time = formatAgendaTime(item.startTime);
              return (
                <p className="schedule-item" key={item.id || index}>
                  <span className="time">{time}</span>
                  {time ? " - " : ""}
                  <span className={!time ? "time" : ""}>{item.title}</span>
                </p>
              );
            })}
          </div>
        </ScheduleItem>
      );
    });
  };

  if (isLoading || gettingDeviceDetails) return <Spinner />;

  return (
    <MainContainer>
      <NavBar>
        <img src={logo} alt="logo" />
      </NavBar>
      <IntroContainer>
        <div className="image-container">
          {attendance?.guestFirstName && (
            <h1>
              {"Welcome,"}{" "}
              <span className="new-line">
                {attendance.guestFirstName} {attendance.guestLastName}
              </span>
            </h1>
          )}
        </div>

        <div className="text-container">
          <h2>{"Servus!"}</h2>
          <p>
            We are excited to have you join us for the{" "}
            <span className="bold">86th Hahnenkamm Races</span> in Kitzbühel.
            Enjoy a premium experience, priceless surprises, and the thrill of
            Streif!
          </p>
        </div>
      </IntroContainer>
      {gateTicketCode && (
        <TicketContainer>
          <div className="ticket-header">
            <img src={ticketLogo} alt="ticket logo" className="ticket-logo" />
            <h2>{"HKR 2026 TICKET"}</h2>
          </div>
          <h3>{"25.01.2026"}</h3>

          <div className="zone">
            <p>{gateTicketZone}</p>
          </div>
          {ticketQR && (
            <img className="qr-code" src={ticketQR} alt="ticket QR" />
          )}
        </TicketContainer>
      )}
      <ScheduleContainer>
        <h2>{"Today's Highlights"}</h2>
        {renderHighlights()}
      </ScheduleContainer>
      <ScheduleInfoContainer>
        <p>
          {
            "The Mastercard Hospitality Team will be available throughout the event, and will be positioned in the hotel lobby 10 minutes prior to all listed meeting times. Please make sure to adhere to the timings as transportation will depart promptly."
          }
        </p>
      </ScheduleInfoContainer>
      <Separator isLeft={true} />
      <DiscoverContainer id="discover">
        <h2>{"Experience Kitz"}</h2>
        <p>{"Tap to find out what there is to discover"}</p>
        <div className="items-container">
          <DiscoverItem
            onClick={() => {
              trackButtonClick("yourStay");
              handleOpen("yourStay");
            }}
          >
            <img src={yourStay} alt="your stay" />
          </DiscoverItem>
          <DiscoverItem onClick={() => handleOpen("skiing")}>
            <img src={skiing} alt="skiing" />
          </DiscoverItem>
          <DiscoverItem onClick={() => handleOpen("balance")}>
            <img src={balance} alt="balance" />
          </DiscoverItem>
          <DiscoverItem onClick={() => handleOpen("memories")}>
            <img src={memories} alt="memories" />
          </DiscoverItem>
        </div>
      </DiscoverContainer>
      <Separator />
      <RacesContainer>
        <h2>{"The Hahnenkamm Races"}</h2>
        <p>{"Peak of Skiing"}</p>
        <p>
          {
            "Attracting 85,000 visitors annually and reaching speeds up to 95mph, the annual "
          }
          <span className="bold">{"Hahnenkamm Races "}</span>
          {
            "in Kitzbühel, Austria are known worldwide as fastest and most spectacular skiing event on the men's FIS Alpine Skiing race calendar."
          }
        </p>
        <p>
          {"As well as being one of the athletic highlights of the "}
          <span className="bold">{"FIS World Cup"}</span>
          {
            ", the Hahnenkamm Races are also an exclusive social event, attracting both sports afficionados and celebrities alike."
          }
        </p>
        <p>
          {"The "}
          <span className="bold">{"three main races"}</span>
          {
            " - Super G (Friday), Downhill (Saturday), and Slalom (Sunday) - will take place in Kitzbühel, Austria on January 23-25, 2026, and will be attended by fans from all over Europe. "
          }
        </p>
        <h3>{"Race Itinerary"}</h3>
        <div className="itinerary-container">
          {RACE_ITINERARY.map((item, index) => (
            <div className="itinerary-item" key={index}>
              <h3>{item.name}</h3>
              <div className="time-event">
                <p className="time">{item.time}</p>
                <p>
                  {" - "}
                  {item.event}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p>{"*standing area tickets available, no lounge access"}</p>
        <p>
          {"Find more information on the official "}
          <a href="https://hahnenkamm.com/en/">{"Hahnenkamm Races Website."}</a>
        </p>
      </RacesContainer>
      <GetToKnowContainer>
        <img src={getToKnow} alt="get to know kitzbuhel" />
        <div className="text-container">
          <h2>{"Get to know Kitzbühel"}</h2>
          <p>{'The "Gamsstadt" awaits'}</p>
        </div>
        <Separator />
        <div className="text-container">
          <p>
            <span className="bold">{"Kitzbühel"}</span>{" "}
            {
              "is a holiday destination in the heart of Tyrol, framed by the Kitzbüheler Horn, the Südberge mountains and the famous Hahnenkamm. If you know Kitzbühel only in wintertime, you still have to discover the majority of the charm of this Alpine region. As"
            }{" "}
            <span className="bold">{"winter sports resort"}</span>{" "}
            {
              'Kitzbühel looks back on a long tradition. However, even before the skiers discovered the so-called "Gamsstadt", guests from near and far came for'
            }{" "}
            <span className="bold">{"summer holidays"}</span>{" "}
            {
              "in Kitzbühel. The good, healthy mountain air, the clear water of the lakes and streams and the gentle, local grass mountains attracted many holidaymakers and hikers already more than a hundred years ago. "
            }
          </p>
          <img src={localAttractions} alt="local attractions" />
          <p>
            <span className="bold">{"Local attractions"}</span>
          </p>
          <p>
            <span className="bold">{"Kitzbühel"}</span> {"unites"}{" "}
            <span className="bold">
              {"urban flair with typical Tyrolean hospitality"}
            </span>
            {
              ", offering a variety of cuisine in addition to first-class shopping opportunities. It’s an enjoyable combination of sports and lifestyle. Kitzbühel’s city centre is"
            }
            <span className="bold">{" quick and easy to navigate"}</span>{" "}
            {
              "and offers the ideal ambience for shopping in the Alps. Window shop international and original "
            }
            <span className="bold">{"Kitzbühel fashion brands"}</span>{" "}
            {
              "in stores like Sportalm, Frauenschuh, Franz Prader or Helmut Eder. A tour of discovery at the craft workshops of the Kitzbühel Master Guild is also worthwhile."
            }
          </p>
        </div>
      </GetToKnowContainer>
      <LanguageGuideContainer>
        <h2>{"Austrian for Beginners"}</h2>
        <p>{"Blend right in with these Austrian phrases:"}</p>
        <p className="bold">{"Servus"}</p>
        <p>{"A common and versatile greeting, similar to “hello” or “hi”."}</p>
        <p className="bold">{"Grüß Gott"}</p>
        <p>{"A more formal greeting, especially in rural areas."}</p>
        <p className="bold">{"Wiederschaun"}</p>
        <p>{"A formal “goodbye” when you are leaving."}</p>
        <p className="bold">{"Pfiat di"}</p>
        <p>{"A less formal “goodbye”."}</p>
        <p className="bold">{"Bist du deppert!"}</p>
        <p>
          {
            "Roughly: “Are you out of your mind?”, often used to express surprise or disbelief."
          }
        </p>
      </LanguageGuideContainer>
      <FooterContainer>
        <p>{"Powered by"}</p>
        <img src={poweredBy} alt="powered by" />
        <p>{"Payment services by"}</p>
        <img src={paymeswissLogo} alt="payment services" />
        <br />
        <p>
          <a href="https://www.mastercard.com/global/en.html">
            {"Privacy Policy"}
          </a>
        </p>
        <p>
          <a href="https://www.mastercard.com/global/en.html">
            {"Terms & Conditions"}
          </a>
        </p>
        {serialNumber && (
          <div className="copyToClipboard">
            <p>ID: {serialNumber}</p>
            <img
              onClick={() => {
                navigator.clipboard.writeText(serialNumber);
                setCopied(true);
              }}
              src={copyIconWhite}
              alt="copy to clipboard"
            />
          </div>
        )}
        <div className="version">
          <p>v.{packageJson.version}</p>
        </div>
        {copied && (
          <p>
            <b>Copied to clipboard</b>
          </p>
        )}
      </FooterContainer>
      {isMemoriesOpen && (
        <Memories
          serialNumber={serialNumber}
          onClose={() => handleClose("memories")}
        />
      )}
      {isBalanceOpen && (
        <Balance
          onClose={() => handleClose("balance")}
          remainingBalance={balanceAndPin?.balanceDetails?.availableBalance}
          activationCode={balanceAndPin?.activationCode}
        />
      )}
      {isSkiingOpen && (
        <Skiing
          onClose={() => handleClose("skiing")}
          onActivateTicket={handleActivateTicket}
          activatingTicket={activatingTicket}
          activatingTicketError={activatingTicketError}
          ticketActive={skiTicket?.active}
          ticketId={skiTicket?.id}
          ticketDate={skiTicket?.date}
          hasTickets={tickets?.length > 0}
          nextTicketDate={nextSkiTicketDate}
          remainingRedemptions={skiTicket?.remainingRedemptions}
        />
      )}

      {isYourStayOpen &&
        (() => {
          const hotelInfo = getHotelInfo();
          if (!hotelInfo) return null;
          return (
            <YourStay
              onClose={() => handleClose("yourStay")}
              hotelInfo={hotelInfo}
              hotelOptions={{
                selectedOptionId: hotelInfo.options?.[0]?.id,
              }}
              walletURL={getWalletURL()}
            />
          );
        })()}
      {weatherData && (
        <div style={{ position: "fixed", top: "90%", right: "10px" }}>
          <WeatherWidget
            weatherData={weatherData}
            loading={loadingWeather}
            error={errorWeather}
          />
        </div>
      )}
    </MainContainer>
  );
};

export default Kitzbuhel26;
